import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import LanguageSwitcher from './LanguageSwitcher';

const categories = [
  { name: 'category_shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { name: 'category_hoodies', url: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { name: 'category_tshirts', url: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { name: 'category_jackets', url: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { name: 'category_pants', url: 'https://kaspreadsheet.com/search-915/name/Shoes.html' },
  { name: 'category_headwear', url: 'https://kaspreadsheet.com/search-916/name/Shoes.html' },
  { name: 'category_sets', url: 'https://kaspreadsheet.com/search-917/name/Shoes.html' },
  { name: 'category_underwear', url: 'https://kaspreadsheet.com/search-918/name/Shoes.html' },
  { name: 'category_jersey', url: 'https://kaspreadsheet.com/search-919/name/Shoes.html' },
  { name: 'category_accessories', url: 'https://kaspreadsheet.com/search-920/name/Shoes.html' },
  { name: 'category_bags', url: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
  { name: 'category_belts', url: 'https://kaspreadsheet.com/search-922/name/Shoes.html' },
  { name: 'category_perfumes', url: 'https://kaspreadsheet.com/search-923/name/Shoes.html' },
  { name: 'category_other', url: 'https://kaspreadsheet.com/search-924/name/Shoes.html' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navLinks = [
    { label: t('nav_guides'), href: '/#articles' },
    { label: t('nav_reviews'), href: '/allchinabuy-review' },
    { label: t('nav_shipping'), href: '/allchinabuy-shipping-cost' },
    { label: t('nav_about'), href: '/about' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm border-b border-stone-100'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-stone-800 flex items-center justify-center">
              <i className="ri-shopping-bag-3-line text-white text-sm"></i>
            </div>
            <span className="text-lg font-semibold text-stone-800 tracking-tight hidden sm:block">
              AllChinaBuy
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900 transition-colors cursor-pointer whitespace-nowrap"
                type="button"
              >
                {t('nav_categories')}
                <i className={`ri-arrow-down-s-line transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-stone-200 py-2 z-50 max-h-[70vh] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      onClick={() => setIsDropdownOpen(false)}
                      className="block px-4 py-2.5 text-sm text-stone-600 hover:text-stone-900 hover:bg-stone-50 transition-colors cursor-pointer"
                    >
                      {t(cat.name)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="text-sm text-stone-600 hover:text-stone-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            <LanguageSwitcher />

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="hidden md:inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-stone-800 text-white rounded-lg hover:bg-stone-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('hero_cta_primary')}
              <i className="ri-external-link-line text-xs"></i>
            </a>

            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-stone-600 hover:text-stone-900 cursor-pointer"
              type="button"
            >
              <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-stone-100 px-4 py-4 space-y-3">
          <p className="text-xs font-medium text-stone-400 uppercase tracking-wider">{t('nav_categories')}</p>
          <div className="grid grid-cols-2 gap-2">
            {categories.map((cat) => (
              <a
                key={cat.name}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 rounded-lg transition-colors cursor-pointer"
              >
                {t(cat.name)}
              </a>
            ))}
          </div>
          <div className="border-t border-stone-100 pt-3 space-y-2">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block px-3 py-2 text-sm text-stone-600 hover:bg-stone-50 rounded-lg transition-colors cursor-pointer"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </div>
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="block text-center px-4 py-2.5 text-sm bg-stone-800 text-white rounded-lg hover:bg-stone-700 transition-colors cursor-pointer"
          >
            {t('hero_cta_primary')}
          </a>
        </div>
      )}
    </nav>
  );
}